import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useContext } from "react";
import { AuthContext } from "./context/AuthContext";
import { useTheme } from "./context/ThemeContext";

export default function VisualizarRotas() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const { isDarkMode } = useTheme();
  const [rotasData, setRotasData] = useState({});
  const [selectedRota, setSelectedRota] = useState(null);
  const [compareMode, setCompareMode] = useState(false);
  const [routesToCompare, setRoutesToCompare] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  // Estado para o sistema de senha
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const correctPassword = 'rotaNova3546';
  const [formData, setFormData] = useState({
    opcao: '',
    nome: '',
    origem: 'SAL EXPRESS',
    destino: '',
    paradas: [],
    modo: 'DRIVING',
    cor: '#0b1a99'
  });
  const [novaParada, setNovaParada] = useState('');
  const [formErrors, setFormErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const mapRef = useRef(null);
  const googleMapRef = useRef(null);
  const directionsServiceRef = useRef(null);
  const directionsRendererRef = useRef(null);
  const markersRef = useRef([]);

  // Função para gerar cores diferentes para cada rota comparada
  const getColorForRoute = (index) => {
    const colors = ['#0b1a99', '#e63946', '#2a9d8f', '#e76f51', '#9d4edd', '#f4a261', '#2b2d42', '#ffb703'];
    return colors[index % colors.length];
  };
  
  useEffect(() => {
    // Verificar se o usuário está autenticado
    if (!user) {
      navigate("/");
    }
    
    // Verificar se está autenticado com a senha
    if (!isAuthenticated) {
      return; // Não carregar mais nada se não estiver autenticado com a senha
    }
  }, [user, navigate, isAuthenticated]);
  
  // Verificar autenticação por senha antes de prosseguir
  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password === correctPassword) {
      setIsAuthenticated(true);
      setPasswordError('');
      localStorage.setItem('rotasAuth', 'true'); // Armazenar autenticação temporariamente
    } else {
      setPasswordError('Senha incorreta. Tente novamente.');
    }
  };
  
  // Verificar se há uma autenticação salva ao carregar a página
  useEffect(() => {
    const savedAuth = localStorage.getItem('rotasAuth');
    if (savedAuth === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  // Limpar o mapa quando o modo de comparação é alterado
  useEffect(() => {
    // Limpar o mapa quando sair do modo de comparação
    if (!compareMode) {
      // Limpar marcadores existentes
      if (markersRef.current.length > 0) {
        markersRef.current.forEach(marker => {
          if (marker) marker.setMap(null);
        });
        markersRef.current = [];
      }
      
      // Limpar referências de caminhos de rotas
      if (window.routePaths) {
        window.routePaths = [];
      }
      if (window.routeIntersections) {
        window.routeIntersections = [];
      }
    }
  }, [compareMode]);

  // Buscar dados das rotas da API
  useEffect(() => {
    const fetchRotas = async () => {
      try {
        setLoading(true);
        const response = await fetch('https://romaneio.salexpress.com.br/roterizacao/processar_rotas.php');
        const data = await response.json();
        setRotasData(data);
        
        // Selecionar o primeiro grupo automaticamente
        const firstGroup = Object.keys(data)[0];
        if (firstGroup) {
          setSelectedGroup(firstGroup);
        }
      } catch (error) {
        console.error('Erro ao buscar rotas:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchRotas();
  }, []);

  // Função para filtrar rotas
  const getFilteredRoutes = () => {
    let allRoutes = [];
    
    if (selectedGroup === 'all') {
      // Mostrar todas as rotas de todos os grupos
      Object.keys(rotasData).forEach(group => {
        rotasData[group].forEach(rota => {
          allRoutes.push({ ...rota, grupo: group });
        });
      });
    } else if (selectedGroup && rotasData[selectedGroup]) {
      // Mostrar rotas do grupo selecionado
      allRoutes = rotasData[selectedGroup].map(rota => ({ ...rota, grupo: selectedGroup }));
    }

    // Filtrar por termo de busca
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      allRoutes = allRoutes.filter(rota => {
        const origem = rota.origem?.toLowerCase() || '';
        const destino = rota.destino?.toLowerCase() || '';
        const nome = rota.nome?.toLowerCase() || '';
        const paradas = rota.paradas?.join(' ').toLowerCase() || '';
        
        return origem.includes(term) || 
               destino.includes(term) || 
               nome.includes(term) || 
               paradas.includes(term);
      });
    }

    return allRoutes;
  };

  // Inicializar o mapa no carregamento do componente
  useEffect(() => {
    const loadGoogleMapsScript = () => {
      // Verificar se a API já está carregada
      if (window.google && window.google.maps) {
        console.log("Google Maps API já está carregada, inicializando mapa diretamente");
        try {
          if (!directionsServiceRef.current) {
            directionsServiceRef.current = new window.google.maps.DirectionsService();
          }
          if (!directionsRendererRef.current) {
            directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
              suppressMarkers: true,
            });
          }
          initMap();
        } catch (error) {
          console.error("Erro ao inicializar serviços do Google Maps:", error);
          // Tentar novamente após um pequeno delay
          setTimeout(loadGoogleMapsScript, 1000);
        }
        return;
      }

      console.log("Carregando Google Maps API script");
      
      // Limpar qualquer script antigo de API que possa estar causando conflito
      const existingScript = document.querySelector('script[src*="maps.googleapis.com/maps/api/js"]');
      if (existingScript) {
        existingScript.remove();
        console.log("Script anterior do Google Maps removido");
      }
      
      // Remover callback anterior para evitar conflitos
      if (window.initGoogleMap) {
        delete window.initGoogleMap;
      }
      
      const googleMapsScript = document.createElement('script');
      googleMapsScript.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyAJgaLbnPer72CcptLqrmvvIM_y7y1rLEI&libraries=places,geometry&callback=initGoogleMap`;
      googleMapsScript.async = true;
      googleMapsScript.defer = true;
      googleMapsScript.onerror = () => {
        console.error("Erro ao carregar o script do Google Maps");
        // Tentar novamente após um pequeno delay
        setTimeout(loadGoogleMapsScript, 2000);
      };
      
      // Definir função de callback global para inicializar o mapa quando o script carregar
      window.initGoogleMap = () => {
        console.log("Google Maps API carregada via callback");
        try {
          if (!directionsServiceRef.current) {
            directionsServiceRef.current = new window.google.maps.DirectionsService();
          }
          if (!directionsRendererRef.current) {
            directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
              suppressMarkers: true,
            });
          }
          
          // Usar setTimeout para garantir que os objetos do Google Maps estejam completamente inicializados
          setTimeout(() => {
            initMap();
            
            // Se já temos uma rota selecionada ou rotas para comparar, renderizá-las
            if (selectedRota || (compareMode && routesToCompare.length > 0)) {
              setTimeout(() => {
                console.log("Renderizando rotas após carregamento da API");
                if (selectedRota) {
                  setSelectedRota({...selectedRota});
                }
                if (compareMode && routesToCompare.length > 0) {
                  setRoutesToCompare([...routesToCompare]);
                }
              }, 1000); // Maior delay para garantir que o mapa esteja pronto
            }
          }, 500);
        } catch (error) {
          console.error("Erro na callback do Google Maps:", error);
        }
      };
      
      // Adicionar o script ao documento
      document.head.appendChild(googleMapsScript);
    };

    // Adicionar uma verificação para certificar-se que o componente está montado
    let isMounted = true;
    
    // Tentar carregar o Google Maps com retry
    const attemptLoadWithRetry = (retryCount = 0) => {
      if (!isMounted) return;
      
      try {
        loadGoogleMapsScript();
      } catch (error) {
        console.error(`Erro ao carregar Google Maps (tentativa ${retryCount + 1}):`, error);
        if (retryCount < 3 && isMounted) {
          setTimeout(() => attemptLoadWithRetry(retryCount + 1), 2000);
        }
      }
    };
    
    attemptLoadWithRetry();

    return () => {
      // Marcar que o componente foi desmontado
      isMounted = false;
      
      // Limpar recursos do mapa ao desmontar
      if (directionsRendererRef.current) {
        try {
          directionsRendererRef.current.setMap(null);
        } catch (error) {
          console.error("Erro ao limpar o renderer:", error);
        }
      }
      
      try {
        markersRef.current.forEach(marker => {
          if (marker) marker.setMap(null);
        });
      } catch (error) {
        console.error("Erro ao limpar marcadores:", error);
      }
      
      markersRef.current = [];
      
      // Remover a função de callback global
      delete window.initGoogleMap;
    };
  }, []);

  const initMap = () => {
    if (!mapRef.current || !window.google || !window.google.maps) {
      console.warn("Não foi possível inicializar o mapa - referências ausentes");
      return;
    }

    try {
      // Forçar uma atualização no container do mapa para garantir que ele tenha dimensões corretas
      mapRef.current.style.width = '100%';
      mapRef.current.style.height = '100%';

      const mapOptions = {
        zoom: 7,
        center: { lat: -21.1767, lng: -45.7089 }, // Centro aproximado de Minas Gerais
        mapTypeId: window.google.maps.MapTypeId.ROADMAP,
        zoomControl: true,
        mapTypeControl: false,
        scaleControl: true,
        streetViewControl: false,
        rotateControl: false,
        fullscreenControl: true
      };

      // Verificar se o mapa já existe para evitar recriação
      if (!googleMapRef.current) {
        googleMapRef.current = new window.google.maps.Map(mapRef.current, mapOptions);
        console.log("Novo mapa criado");
      }
      
      // Reconectar o renderer ao mapa
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(googleMapRef.current);
      }

      console.log("Mapa inicializado com sucesso");
    } catch (error) {
      console.error("Erro ao inicializar o mapa:", error);
    }
  };

  const getSalExpressCoordinates = (grupo) => {
    // Determinar as coordenadas do SAL EXPRESS com base no grupo
    if (grupo) {
      const grupoBase = grupo.substring(0, 3); // Extrair os primeiros 3 caracteres (VGA, POA, DIE, etc.)
      
      if (grupoBase === 'VGA' || grupo === 'NOVA VGA') {
        return { lat: -21.6243117, lng: -45.4177934 }; // VGA/VGE/NOVA VGA
      } else if (grupoBase === 'POA' || grupoBase === 'POE') {
        return { lat: -22.2664407, lng: -46.0138242 }; // POA/POE
      } else if (grupoBase === 'DIE' || grupo === 'NOVA DIE') {
        return { lat: -20.1334534, lng: -44.9159351 }; // DIE/NOVA DIE
      }
    }
    
    // Coordenadas padrão caso não corresponda a nenhum dos grupos acima
    return { lat: -21.1767, lng: -45.7089 };
  };
  
  // Função para criar o link do Google Maps com a rota
  const createGoogleMapsLink = (rota) => {
    if (!rota || !rota.destino) return '#';
    
    // Obter as coordenadas da origem (Sal Express)
    const origem = getSalExpressCoordinates(rota.grupo || rota.opcao);
    
    // Criar a parte das paradas/destino (adicionando MG em cada cidade)
    let destinations = [];
    
    // Adicionar paradas intermediárias
    if (rota.paradas && rota.paradas.length > 0) {
      destinations = rota.paradas.map(cidade => `${cidade},+MG`);
    }
    
    // Adicionar o destino final
    destinations.push(`${rota.destino},+MG`);
    
    // Criar a URL completa
    const url = `https://www.google.com/maps/dir/${origem.lat},${origem.lng}/${destinations.join('/')}`;
    
    return url;
  };
  
  const fetchCoordinates = async (location) => {
    try {
      const response = await fetch(`https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(location + ", MG, Brasil")}&key=AIzaSyAJgaLbnPer72CcptLqrmvvIM_y7y1rLEI`);
      const data = await response.json();
      
      if (data.status === "OK" && data.results.length > 0) {
        const { lat, lng } = data.results[0].geometry.location;
        return { lat, lng };
      }
      throw new Error(`Não foi possível encontrar coordenadas para ${location}`);
    } catch (error) {
      console.error(`Erro ao buscar coordenadas para ${location}:`, error);
      // Retorna coordenadas aleatórias em MG como fallback
      return { 
        lat: -21.1767 + (Math.random() * 2 - 1), 
        lng: -45.7089 + (Math.random() * 2 - 1) 
      };
    }
  };

  // Renderizar a rota no mapa quando uma rota é selecionada ou quando as rotas de comparação mudam
  useEffect(() => {
    console.log("useEffect acionado, compareMode:", compareMode, "routesToCompare:", routesToCompare.length, "selectedRota:", selectedRota ? selectedRota.nome : null);
    
    // Se não há rota selecionada e não estamos em modo de comparação com rotas, não fazer nada
    if (!selectedRota && (!compareMode || routesToCompare.length === 0)) {
      console.log("Nenhuma rota para mostrar, retornando...");
      return;
    }
    
    // Verificar se o Google Maps está disponível antes de prosseguir
    if (!window.google || !window.google.maps) {
      console.log("Google Maps ainda não está disponível, retornando...");
      return;
    }
    
    // Limpar mapa antes de renderizar novas rotas
    if (markersRef.current.length > 0) {
      console.log("Limpando marcadores existentes:", markersRef.current.length);
      try {
        markersRef.current.forEach(marker => {
          if (marker) marker.setMap(null);
        });
      } catch (error) {
        console.error("Erro ao limpar marcadores:", error);
      }
      markersRef.current = [];
    }
    
    console.log("useEffect para renderizar rota executando", compareMode ? "Modo comparação" : "Modo normal");
    
    // Função para tentar renderizar a rota várias vezes, em caso de falhas
    const attemptRenderRoute = async (attempt = 0) => {
      console.log(`Tentativa ${attempt + 1} de renderizar rota(s)`);
      
      // Verificar se o Google Maps está carregado
      if (!window.google || !window.google.maps) {
        console.log("Google Maps API não está carregada, tentando novamente em 1000ms...");
        if (attempt < 5) {
          setTimeout(() => attemptRenderRoute(attempt + 1), 1000);
        }
        return;
      }
      
      // Verificar se os serviços de direção estão inicializados
      if (!directionsServiceRef.current) {
        console.log("Criando serviço de direções");
        try {
          directionsServiceRef.current = new window.google.maps.DirectionsService();
        } catch (error) {
          console.error("Erro ao criar serviço de direções:", error);
          if (attempt < 5) {
            setTimeout(() => attemptRenderRoute(attempt + 1), 1000);
          }
          return;
        }
      }
      
      // No modo de comparação, não usamos o renderer, pois cada rota terá sua própria linha
      if (!compareMode && !directionsRendererRef.current) {
        console.log("Criando renderer de direções");
        try {
          directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
            suppressMarkers: true,
          });
        } catch (error) {
          console.error("Erro ao criar renderer de direções:", error);
          if (attempt < 5) {
            setTimeout(() => attemptRenderRoute(attempt + 1), 1000);
          }
          return;
        }
      }
      
      // Verificar se o mapa está inicializado
      if (!googleMapRef.current && mapRef.current) {
        console.log("Inicializando mapa");
        initMap();
      }
      
      // Verificar se o mapa está pronto
      if (!googleMapRef.current) {
        console.log("Mapa ainda não está pronto, tentando novamente em 1000ms...");
        if (attempt < 5) {
          setTimeout(() => attemptRenderRoute(attempt + 1), 1000);
        }
        return;
      }
      
      // Associar o renderer ao mapa (apenas no modo normal)
      try {
        if (!compareMode && directionsRendererRef.current && googleMapRef.current) {
          directionsRendererRef.current.setMap(googleMapRef.current);
        } else if (compareMode && directionsRendererRef.current) {
          // No modo de comparação, não utilizamos o renderer para evitar conflitos
          directionsRendererRef.current.setMap(null);
        }
      } catch (error) {
        console.error("Erro ao associar renderer ao mapa:", error);
        if (attempt < 5) {
          setTimeout(() => attemptRenderRoute(attempt + 1), 1000);
        }
        return;
      }
      
      try {
        // Limpar os marcadores e polylines existentes
        try {
          markersRef.current.forEach(marker => {
            if (marker) marker.setMap(null);
          });
        } catch (error) {
          console.error("Erro ao limpar marcadores:", error);
        }
        markersRef.current = [];
        
        // Criar um bounds para centralizar o mapa depois
        const bounds = new window.google.maps.LatLngBounds();
        
        if (compareMode && routesToCompare.length > 0) {
          // Modo de comparação - renderizar várias rotas
          console.log("Renderizando em modo de comparação com", routesToCompare.length, "rotas");
          window.routePaths = []; // Resetar caminhos de rotas para evitar referências antigas
          window.routeIntersections = [];
          
          for (let i = 0; i < routesToCompare.length; i++) {
            const rota = routesToCompare[i];
            console.log(`Renderizando rota ${i+1}/${routesToCompare.length}: ${rota.nome}`);
            try {
              await renderSingleRoute(rota, bounds, i);
            } catch (error) {
              console.error(`Erro ao renderizar rota ${rota.nome}:`, error);
            }
          }
        } else if (selectedRota) {
          // Modo normal - renderizar uma única rota
          try {
            await renderSingleRoute(selectedRota, bounds);
          } catch (error) {
            console.error(`Erro ao renderizar rota ${selectedRota.nome}:`, error);
          }
        }
        
        // Centralizar o mapa para mostrar todas as rotas
        try {
          if (bounds.isEmpty()) {
            googleMapRef.current.setCenter({ lat: -21.1767, lng: -45.7089 });
            googleMapRef.current.setZoom(7);
          } else {
            googleMapRef.current.fitBounds(bounds);
            // Adicionar um pequeno padding para não cortar as bordas
            setTimeout(() => {
              try {
                if (googleMapRef.current.getZoom() > 0) {
                  googleMapRef.current.setZoom(googleMapRef.current.getZoom() - 0.5);
                }
              } catch (error) {
                console.error("Erro ao ajustar zoom:", error);
              }
            }, 200);
          }
          console.log("Mapa centralizado para mostrar todas as rotas");
        } catch (error) {
          console.error("Erro ao centralizar o mapa:", error);
        }
        
      } catch (error) {
        console.error('Erro ao renderizar rota:', error);
        if (attempt < 5) {
          console.log("Tentando novamente em 1s...");
          setTimeout(() => attemptRenderRoute(attempt + 1), 1000);
        }
      }
    };
    
    // Função para renderizar uma única rota, usada tanto no modo normal quanto no de comparação
    const renderSingleRoute = async (rota, bounds, routeIndex = 0) => {
      // Obter coordenadas do SAL EXPRESS com base no grupo
      const origem = getSalExpressCoordinates(rota.grupo || rota.opcao);
      console.log(`Rota ${rota.nome}: Origem:`, origem, "Grupo:", rota.grupo || rota.opcao);
      
      // Obter coordenadas do destino
      console.log(`Rota ${rota.nome}: Buscando coordenadas para destino:`, rota.destino);
      let destino;
      if (rota.destino) {
        destino = await fetchCoordinates(rota.destino);
        console.log(`Rota ${rota.nome}: Coordenadas do destino:`, destino);
      } else {
        throw new Error(`Destino não encontrado para rota ${rota.nome}`);
      }
      
      // Preparar paradas
      let waypoints = [];
      if (rota.paradas && rota.paradas.length > 0) {
        console.log(`Rota ${rota.nome}: Processando paradas:`, rota.paradas);
        // Processar apenas as primeiras 8 paradas (limite do Google Maps na versão gratuita)
        const maxWaypoints = Math.min(rota.paradas.length, 8);
        
        for (let i = 0; i < maxWaypoints; i++) {
          console.log(`Rota ${rota.nome}: Buscando coordenadas para parada:`, rota.paradas[i]);
          const coords = await fetchCoordinates(rota.paradas[i]);
          console.log(`Rota ${rota.nome}: Coordenadas para parada:`, coords);
          waypoints.push({
            location: new window.google.maps.LatLng(coords.lat, coords.lng),
            stopover: true
          });
        }
      }
      
      // Configurar o modo de viagem
      const travelMode = rota.modo === 'DRIVING' 
        ? window.google.maps.TravelMode.DRIVING 
        : rota.modo === 'WALKING' 
          ? window.google.maps.TravelMode.WALKING 
          : rota.modo === 'BICYCLING' 
            ? window.google.maps.TravelMode.BICYCLING 
            : window.google.maps.TravelMode.TRANSIT;
      
      console.log(`Rota ${rota.nome}: Solicitando cálculo de rota com modo:`, travelMode);
      
      // Fazer a requisição para o serviço de direções
      return new Promise((resolve, reject) => {
        directionsServiceRef.current.route({
          origin: new window.google.maps.LatLng(origem.lat, origem.lng),
          destination: new window.google.maps.LatLng(destino.lat, destino.lng),
          waypoints: waypoints,
          optimizeWaypoints: true,
          travelMode: travelMode
        }, (response, status) => {
          console.log(`Rota ${rota.nome}: Resposta do serviço de direções:`, status);
          
          if (status === 'OK') {
            if (compareMode) {
              // No modo de comparação, desenhar a rota com uma polyline
              const routeColor = rota.cor || getColorForRoute(routeIndex);
              
              const path = response.routes[0].overview_path;
              const polyline = new window.google.maps.Polyline({
                path: path,
                strokeColor: routeColor,
                strokeOpacity: 0.8,
                strokeWeight: 5,
                zIndex: 10 + routeIndex, // Ajuste de z-index para evitar sobreposição
                icons: [{
                  icon: {
                    path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                    scale: 3,
                    strokeColor: '#FFFFFF',
                    strokeWeight: 1,
                    fillColor: routeColor,
                    fillOpacity: 1
                  },
                  offset: '50%',
                  repeat: '100px'
                }]
              });
              
              try {
                polyline.setMap(googleMapRef.current);
                console.log(`Rota ${rota.nome}: Polyline adicionada ao mapa`);
                markersRef.current.push(polyline); // Armazenar para limpar depois
                
                // Adicionar pontos de origem, destino e paradas
                addRouteMarkers(origem, destino, response, rota, routeColor, routeIndex);
                
                // Encontrar e marcar intersecções entre rotas
                if (routesToCompare.length > 1) {
                  findAndMarkIntersections(path, routeIndex);
                }
              } catch (e) {
                console.error("Erro ao adicionar polyline ao mapa:", e);
              }
            } else {
              // No modo normal, usar o renderer
              directionsRendererRef.current.setDirections(response);
              console.log(`Rota ${rota.nome}: Rota configurada no renderer`);
              
              // Adicionar marcadores personalizados
              addRouteMarkers(origem, destino, response, rota);
            }
            
            // Atualizar bounds para incluir esta rota
            bounds.extend(new window.google.maps.LatLng(origem.lat, origem.lng));
            bounds.extend(new window.google.maps.LatLng(destino.lat, destino.lng));
            waypoints.forEach(waypoint => {
              bounds.extend(waypoint.location);
            });
            
            resolve();
          } else {
            console.error(`Rota ${rota.nome}: Erro ao calcular rota:`, status);
            reject(new Error(`Não foi possível calcular a rota ${rota.nome}: ${status}`));
          }
        });
      });
    };
    
    // Função auxiliar para adicionar marcadores a uma rota
    const addRouteMarkers = (origem, destino, response, rota, routeColor = null, routeIndex = 0) => {
      const color = routeColor || rota.cor || '#0b1a99';
      
      const addMarker = (position, title, icon, info = null) => {
        let markerIcon;
        
        if (icon === 'start') {
          // Ícone de origem (ponto de saída) diferente
          markerIcon = {
            path: window.google.maps.SymbolPath.BACKWARD_CLOSED_ARROW,
            fillColor: '#005500',
            fillOpacity: 1,
            strokeWeight: 2,
            strokeColor: '#ffffff',
            scale: 6,
            rotation: 270
          };
        } else if (icon === 'end') {
          // Ícone para destino
          markerIcon = {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: color,
            fillOpacity: 1,
            strokeWeight: 2,
            strokeColor: '#ffffff',
            scale: 8
          };
        } else if (icon === 'intersection') {
          // Ícone para intersecções - cruz grande
          markerIcon = {
            path: 'M-5,0 L5,0 M0,-5 L0,5',
            strokeColor: '#FFFFFF',
            strokeWeight: 3,
            scale: 1.5
          };
        } else {
          // Ícones para pontos de parada
          markerIcon = {
            path: window.google.maps.SymbolPath.CIRCLE,
            fillColor: color,
            fillOpacity: 1,
            strokeWeight: 1,
            strokeColor: '#ffffff',
            scale: 6
          };
        }
        
        const marker = new window.google.maps.Marker({
          position,
          map: googleMapRef.current,
          title,
          icon: markerIcon,
          zIndex: icon === 'intersection' ? 200 : (icon === 'start' ? 150 : (icon === 'end' ? 100 : 50))
        });
        
        // Adicionar InfoWindow para mostrar informações ao clicar no marcador
        const infoContent = info ? 
          `<div style="font-weight:bold;">${title}</div><div>${info}</div>` : 
          `<div style="font-weight:bold;">${title}</div>`;
          
        const infoWindow = new window.google.maps.InfoWindow({
          content: infoContent
        });
        
        marker.addListener('click', () => {
          infoWindow.open(googleMapRef.current, marker);
        });
        
        markersRef.current.push(marker);
      };
      
      // Marcadores de origem e destino
      addMarker(
        new window.google.maps.LatLng(origem.lat, origem.lng), 
        'Origem', 
        'start', 
        compareMode ? `Rota: ${rota.nome}` : null
      );
      
      addMarker(
        new window.google.maps.LatLng(destino.lat, destino.lng), 
        'Destino', 
        'end', 
        compareMode ? `Rota: ${rota.nome}` : null
      );
      
      // Marcadores para cada parada
      if (response.routes[0].legs.length > 1) {
        response.routes[0].legs.forEach((leg, i) => {
          if (i < response.routes[0].legs.length - 1) {
            addMarker(
              leg.end_location, 
              `Parada ${i+1}`, 
              'waypoint', 
              compareMode ? `Rota: ${rota.nome}` : null
            );
          }
        });
      }
    };
    
    // Iniciar a tentativa de renderização com um pequeno delay para garantir que tudo está pronto
    setTimeout(() => {
      attemptRenderRoute();
    }, 300);
    
    // Cleanup
    return () => {
      console.log("Limpando recursos da rota");
      if (directionsRendererRef.current) {
        directionsRendererRef.current.setMap(null);
      }
      markersRef.current.forEach(marker => {
        if (marker) marker.setMap(null);
      });
      markersRef.current = [];
    };
  }, [selectedRota, compareMode, routesToCompare]);

  // Reiniciar o mapa em caso de problemas
  const resetMap = () => {
    console.log("Reiniciando o mapa...");
    
    // Limpar marcadores e polylines existentes
    if (markersRef.current.length > 0) {
      markersRef.current.forEach(marker => {
        if (marker) marker.setMap(null);
      });
      markersRef.current = [];
    }
    
    // Resetar referências globais
    window.routePaths = [];
    window.routeIntersections = [];
    
    // Se estiver em modo normal, reiniciar o renderer
    if (!compareMode && directionsRendererRef.current) {
      directionsRendererRef.current.setMap(null);
      directionsRendererRef.current = new window.google.maps.DirectionsRenderer({
        suppressMarkers: true,
      });
      directionsRendererRef.current.setMap(googleMapRef.current);
    }
    
    // Forçar re-renderização
    if (compareMode && routesToCompare.length > 0) {
      setRoutesToCompare([...routesToCompare]);
    } else if (selectedRota) {
      setSelectedRota({...selectedRota});
    }
    
    console.log("Mapa reiniciado com sucesso");
  };

  const filteredRoutes = getFilteredRoutes();
  
  // Funções para o formulário de adição/edição de rota
  const handleOpenModal = (rota = null) => {
    if (rota) {
      // Editar rota existente
      setFormData({
        opcao: rota.grupo || selectedGroup,
        nome: rota.nome,
        origem: rota.origem,
        destino: rota.destino,
        paradas: [...(rota.paradas || [])],
        modo: rota.modo,
        cor: rota.cor
      });
    } else {
      // Nova rota
      setFormData({
        opcao: selectedGroup !== 'all' ? selectedGroup : Object.keys(rotasData)[0] || '',
        nome: '',
        origem: 'SAL EXPRESS',
        destino: '',
        paradas: [],
        modo: 'DRIVING',
        cor: '#0b1a99'
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setFormData({
      opcao: '',
      nome: '',
      origem: 'SAL EXPRESS',
      destino: '',
      paradas: [],
      modo: 'DRIVING',
      cor: '#0b1a99'
    });
    setNovaParada('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const handleAddParada = () => {
    if (novaParada.trim()) {
      setFormData({
        ...formData,
        paradas: [...formData.paradas, novaParada.trim()]
      });
      setNovaParada('');
    }
  };

  const handleRemoveParada = (index) => {
    const updatedParadas = [...formData.paradas];
    updatedParadas.splice(index, 1);
    setFormData({
      ...formData,
      paradas: updatedParadas
    });
  };

  const validateForm = () => {
    const errors = {};
    
    if (!formData.opcao) errors.opcao = 'Grupo é obrigatório';
    if (!formData.nome) errors.nome = 'Nome é obrigatório';
    if (!formData.destino) errors.destino = 'Destino é obrigatório';
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;
    
    try {
      setIsSaving(true);
      
      const response = await fetch('https://romaneio.salexpress.com.br/roterizacao/processar_rotas.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData)
      });
      
      if (!response.ok) {
        throw new Error('Erro ao salvar rota');
      }
      
      // Atualizar o estado local em vez de buscar os dados novamente
      // Isso evita o travamento causado pela consulta duplicada
      const newRota = { ...formData };
      setRotasData(prevData => {
        const updatedData = { ...prevData };
        
        // Se o grupo já existe, adicione/atualize a rota
        if (updatedData[formData.opcao]) {
          // Verificar se é atualização ou nova rota
          const existingIndex = updatedData[formData.opcao].findIndex(r => r.nome === formData.nome);
          
          if (existingIndex >= 0) {
            // Atualizar rota existente
            updatedData[formData.opcao][existingIndex] = newRota;
          } else {
            // Adicionar nova rota
            updatedData[formData.opcao].push(newRota);
          }
        } else {
          // Criar novo grupo com a rota
          updatedData[formData.opcao] = [newRota];
        }
        
        return updatedData;
      });
      
      // Se estiver editando uma rota, atualizar a rota selecionada
      if (selectedRota && selectedRota.nome === formData.nome) {
        setSelectedRota(newRota);
      }
      
      handleCloseModal();
      
    } catch (error) {
      console.error('Erro ao salvar rota:', error);
      alert('Ocorreu um erro ao salvar a rota. Tente novamente.');
    } finally {
      setIsSaving(false);
    }
  };

  // Função para encontrar e marcar interseções entre rotas
  const findAndMarkIntersections = (path, routeIndex) => {
    // Armazenar os caminhos de todas as rotas para comparação
    if (!window.routePaths) {
      window.routePaths = [];
      window.routeIntersections = [];
    }
    
    // Armazenar o caminho atual
    window.routePaths[routeIndex] = path;
    
    // Verificar interseções apenas se tivermos pelo menos duas rotas
    if (routeIndex === 0 || window.routePaths.length <= 1) return;
    
    // Para cada rota anterior, encontrar pontos em comum
    for (let i = 0; i < routeIndex; i++) {
      const otherPath = window.routePaths[i];
      if (!otherPath) continue;
      
      // Encontrar todos os pontos que estão muito próximos entre as rotas
      const commonPoints = findCommonPoints(path, otherPath);
      
      if (commonPoints.length > 0) {
        // Colorir os segmentos de rota que são comuns
        for (let j = 0; j < commonPoints.length; j++) {
          const point = commonPoints[j];
          
          // Para cada ponto comum, criar um pequeno segmento colorido
          const intersectionSegment = new window.google.maps.Polyline({
            path: [
              point.point1,
              point.point2
            ],
            strokeColor: '#FF9900', // Cor da interseção
            strokeOpacity: 1.0,
            strokeWeight: 7, // Mais espesso para destacar
            zIndex: 1001, // Garantir que fique acima das rotas
            map: googleMapRef.current
          });
          
          // Adicionar ao array de marcadores para poder limpar depois
          markersRef.current.push(intersectionSegment);
          
          // Guardar a referência da interseção
          if (!window.routeIntersections[i]) window.routeIntersections[i] = [];
          if (!window.routeIntersections[routeIndex]) window.routeIntersections[routeIndex] = [];
          
          window.routeIntersections[i].push({
            point: point,
            segment: intersectionSegment,
            otherRouteIndex: routeIndex
          });
          
          window.routeIntersections[routeIndex].push({
            point: point,
            segment: intersectionSegment,
            otherRouteIndex: i
          });
        }
      }
    }
  };
  
  // Função para encontrar pontos em comum entre duas rotas
  const findCommonPoints = (path1, path2) => {
    const commonPoints = [];
    const threshold = 0.0003; // Tolerância para considerar pontos próximos (aproximadamente 30-50 metros)
    
    // Verificar cada ponto do caminho 1 contra cada ponto do caminho 2
    for (let i = 0; i < path1.length - 1; i++) {
      const a1 = path1[i];
      const a2 = path1[i + 1];
      
      for (let j = 0; j < path2.length - 1; j++) {
        const b1 = path2[j];
        const b2 = path2[j + 1];
        
        // Verificar se os pontos estão próximos o suficiente
        if (arePointsClose(a1, b1, threshold) && arePointsClose(a2, b2, threshold)) {
          // Pontos de início e fim do segmento são próximos - é um segmento comum
          commonPoints.push({
            point1: a1,
            point2: a2
          });
        }
        else if (arePointsClose(a1, b2, threshold) && arePointsClose(a2, b1, threshold)) {
          // Pontos estão em ordem inversa, mas são próximos
          commonPoints.push({
            point1: a1,
            point2: a2
          });
        }
      }
    }
    
    return commonPoints;
  };
  
  // Função auxiliar para verificar se dois pontos estão próximos
  const arePointsClose = (point1, point2, threshold) => {
    return Math.abs(point1.lat() - point2.lat()) < threshold && 
           Math.abs(point1.lng() - point2.lng()) < threshold;
  };
  
  // Função auxiliar para encontrar interseções entre dois caminhos
  const findPathIntersections = (path1, path2) => {
    const intersections = [];
    const threshold = 0.0001; // Tolerância para considerar pontos próximos como interseção
    
    // Verificar cada segmento de linha do caminho 1 contra cada segmento do caminho 2
    for (let i = 0; i < path1.length - 1; i++) {
      const a = path1[i];
      const b = path1[i + 1];
      
      for (let j = 0; j < path2.length - 1; j++) {
        const c = path2[j];
        const d = path2[j + 1];
        
        // Verificar se os segmentos de linha se cruzam
        const intersection = getLineIntersection(
          a.lat(), a.lng(), b.lat(), b.lng(),
          c.lat(), c.lng(), d.lat(), d.lng(),
          threshold
        );
        
        if (intersection) {
          // Verificar se esta interseção já foi adicionada (evitar duplicatas)
          const isDuplicate = intersections.some(existing => 
            Math.abs(existing.lat() - intersection.lat()) < threshold && 
            Math.abs(existing.lng() - intersection.lng()) < threshold
          );
          
          if (!isDuplicate) {
            intersections.push(intersection);
          }
        }
      }
    }
    
    return intersections;
  };
  
  // Função para calcular a interseção entre dois segmentos de linha
  const getLineIntersection = (a1, a2, b1, b2, c1, c2, d1, d2, threshold) => {
    // Converter coordenadas para o sistema cartesiano para simplificar o cálculo
    // Usamos a fórmula de interseção de linhas no plano cartesiano
    
    // Vetores direcionais dos segmentos
    const r = { x: b1 - a1, y: b2 - a2 };
    const s = { x: d1 - c1, y: d2 - c2 };
    
    // Coeficientes para a equação paramétrica
    const numerator = (c2 - a2) * (b1 - a1) - (c1 - a1) * (b2 - a2);
    const denominator = (d2 - c2) * (b1 - a1) - (d1 - c1) * (b2 - a2);
    
    // Linhas paralelas ou coincidentes
    if (Math.abs(denominator) < threshold) return null;
    
    const t = numerator / denominator;
    
    // Verificar se a interseção está dentro dos segmentos
    if (t < 0 || t > 1) return null;
    
    // Calcular o ponto de interseção
    const intersectionLat = c1 + t * (d1 - c1);
    const intersectionLng = c2 + t * (d2 - c2);
    
    // Criar e retornar um objeto LatLng para a interseção
    return new window.google.maps.LatLng(intersectionLat, intersectionLng);
  };
  
  return (
    <div className="flex h-screen bg-background dark:bg-dark-background overflow-hidden">
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="bg-white-whiter dark:bg-dark-surface shadow-sm border-b border-gray-200 dark:border-dark-border p-6 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-text dark:text-dark-text">
                Visualizar Rotas
              </h1>
              <p className="text-textSecondary dark:text-dark-textSecondary mt-1">
                Gerencie e visualize todas as rotas cadastradas
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-textSecondary dark:text-dark-textSecondary">
                Total de rotas
              </p>
              <p className="text-2xl font-bold text-salBlue dark:text-salBlue">
                {selectedGroup === 'all' ? Object.values(rotasData).flat().length : (selectedGroup ? rotasData[selectedGroup]?.length || 0 : Object.values(rotasData).flat().length)}
              </p>
            </div>
          </div>
        </div>

        <div className="flex flex-1 overflow-hidden">
          {/* Painel lateral com lista de rotas */}
          <div className="w-80 bg-white-whiter dark:bg-dark-surface border-r border-gray-200 dark:border-dark-border flex flex-col overflow-hidden">
            <div className="p-4 flex-1 overflow-y-auto">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-text dark:text-dark-text">
                  Grupos de Rotas
                </h2>
                <button 
                  onClick={() => {
                    setCompareMode(!compareMode);
                    if (compareMode) {
                      // Saindo do modo de comparação
                      setRoutesToCompare([]);
                      if (directionsRendererRef.current) {
                        directionsRendererRef.current.setMap(googleMapRef.current);
                      }
                    } else {
                      // Entrando no modo de comparação
                      setSelectedRota(null);
                      if (directionsRendererRef.current) {
                        directionsRendererRef.current.setMap(null);
                      }
                    }
                  }}
                  className={`text-xs px-2 py-1 rounded ${
                    compareMode
                      ? "bg-red-500 hover:bg-red-600 text-white"
                      : "bg-salBlue hover:bg-salBlue/90 text-white"
                  }`}
                >
                  {compareMode ? "Cancelar Comparação" : "Comparar Rotas"}
                </button>
              </div>
                
              <div className="mb-4 space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <label className="text-sm font-medium text-text dark:text-dark-text mb-2 block">
                      Grupo
                    </label>
                    <select 
                      value={selectedGroup} 
                      onChange={(e) => setSelectedGroup(e.target.value)}
                      className="w-full p-2 rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card text-text dark:text-dark-text text-sm"
                    >
                      <option value="">Selecione um grupo</option>
                      <option value="all">Todas as rotas ({Object.values(rotasData).flat().length})</option>
                      {Object.keys(rotasData).map((group) => (
                        <option key={group} value={group}>
                          {group} ({rotasData[group]?.length || 0} rotas)
                        </option>
                      ))}
                    </select>
                  </div>
                  <button 
                    onClick={() => handleOpenModal()}
                    className="h-8 px-4 bg-salBlue text-white rounded-lg hover:bg-salBlue/90 transition-colors flex items-center mt-7 font-medium shadow-sm hover:shadow"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 mr-2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M10 4.5v15m7.5-7.5h-15" />
                    </svg>
                    Nova
                  </button>
                </div>
                
                <div>
                  <label className="text-sm font-medium text-text dark:text-dark-text mb-2 block">
                    Buscar por cidade
                  </label>
                  <input
                    type="text"
                    placeholder="Digite o nome da cidade..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      // Quando começar a pesquisar, selecionar "todas as rotas"
                      if (e.target.value && selectedGroup !== 'all') {
                        setSelectedGroup('all');
                      }
                    }}
                    className="w-full p-2 rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card text-text dark:text-dark-text text-sm placeholder-textSecondary dark:placeholder-dark-textSecondary"
                  />
                </div>
              </div>

              {compareMode && (
                <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                  <h3 className="font-medium text-sm text-blue-800 dark:text-blue-300 mb-2">
                    Modo de Comparação
                  </h3>
                  <p className="text-xs text-blue-700 dark:text-blue-400 mb-2">
                    Selecione até 5 rotas para comparar no mapa. Clique em uma rota para adicioná-la ou removê-la da comparação.
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-blue-700 dark:text-blue-400">
                      {routesToCompare.length} de 5 rotas selecionadas
                    </span>
                  </div>
                </div>
              )}

              {loading ? (
                <div className="text-center py-4">
                  <p className="text-textSecondary dark:text-dark-textSecondary">Carregando rotas...</p>
                </div>
              ) : (
                <div className="space-y-3 max-h-[calc(100vh-360px)] overflow-y-auto">
                  {filteredRoutes.length > 0 ? (
                    filteredRoutes.map((rota, index) => (
                      <div
                        key={`${rota.grupo || selectedGroup}-${index}`}
                        onClick={() => {
                          if (compareMode) {
                            // Em modo de comparação, adicionar ou remover da lista de comparação
                            const isAlreadySelected = routesToCompare.some(r => 
                              r.nome === rota.nome && r.grupo === rota.grupo
                            );
                            
                            if (isAlreadySelected) {
                              // Remover da comparação
                              setRoutesToCompare(prev => 
                                prev.filter(r => !(r.nome === rota.nome && r.grupo === rota.grupo))
                              );
                            } else {
                              // Adicionar à comparação (limite de 5 rotas)
                              if (routesToCompare.length < 5) {
                                const updatedRoutes = [...routesToCompare, rota];
                                setRoutesToCompare(updatedRoutes);
                                console.log("Adicionada rota à comparação:", rota.nome, "Total:", updatedRoutes.length);
                              } else {
                                alert('Você pode comparar no máximo 5 rotas de uma vez.');
                              }
                            }
                          } else {
                            // Modo normal, selecionar uma única rota
                            setSelectedRota(rota);
                            console.log("Rota selecionada:", rota);
                          }
                        }}
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all duration-200 ${
                          compareMode 
                            ? (routesToCompare.some(r => r.nome === rota.nome && r.grupo === rota.grupo)
                              ? "border-salBlue bg-salBlue/5 dark:bg-salBlue/10"
                              : "border-gray-200 dark:border-dark-border hover:border-salBlue/50 bg-white dark:bg-dark-card")
                            : (selectedRota === rota
                              ? "border-salBlue bg-salBlue/5 dark:bg-salBlue/10"
                              : "border-gray-200 dark:border-dark-border hover:border-salBlue/50 bg-white dark:bg-dark-card")
                        }`}
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h3 className="font-medium text-text dark:text-dark-text truncate">
                            {rota.nome}
                          </h3>
                          <div className="flex items-center gap-2">
                            {selectedGroup === 'all' && (
                              <span className="text-xs bg-gray-100 dark:bg-dark-border px-2 py-1 rounded text-textSecondary dark:text-dark-textSecondary">
                                {rota.grupo}
                              </span>
                            )}
                            <div 
                              className="w-4 h-4 rounded-full border border-gray-300" 
                              style={{ backgroundColor: rota.cor }}
                            ></div>
                          </div>
                        </div>
                        <div className="text-sm text-textSecondary dark:text-dark-textSecondary space-y-1">
                          <p>
                            <span className="font-medium">De:</span> {rota.origem}
                          </p>
                          <p>
                            <span className="font-medium">Para:</span> {rota.destino}
                          </p>
                          <p>
                            <span className="font-medium">Modo:</span> {rota.modo}
                          </p>
                          {rota.paradas && rota.paradas.length > 0 && (
                            <p>
                              <span className="font-medium">Paradas:</span> {rota.paradas.length}
                            </p>
                          )}
                          <div className="mt-2">
                            <a 
                              href={createGoogleMapsLink(rota)} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-salBlue hover:underline flex items-center text-xs"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="w-4 h-4 mr-1">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                              </svg>
                              Ver no Google Maps
                            </a>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="text-center py-4">
                      <p className="text-textSecondary dark:text-dark-textSecondary">
                        {searchTerm ? 'Nenhuma rota encontrada com esse termo de busca' : (selectedGroup ? 'Nenhuma rota encontrada neste grupo' : 'Selecione um grupo para ver as rotas')}
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Área principal do mapa */}
          <div className="flex-1 relative overflow-hidden">
            <div className="h-full bg-gray-100 dark:bg-dark-card flex items-center justify-center overflow-y-auto">
              {/* O mapa sempre visível */}
              <div className="w-full h-full flex flex-col">
                {selectedRota || (compareMode && routesToCompare.length > 0) ? (
                  <div className="bg-white dark:bg-dark-surface p-2 rounded-t-lg border-b border-gray-200 dark:border-dark-border flex justify-between items-center">
                    <h3 className="text-lg font-semibold text-text dark:text-dark-text">
                      {compareMode 
                        ? `Comparando ${routesToCompare.length} rotas` 
                        : `${selectedRota.nome} - ${selectedRota.origem} para ${selectedRota.destino}`
                      }
                    </h3>
                    {compareMode && (
                      <button 
                        onClick={() => {
                          setCompareMode(false);
                          setSelectedRota(null);
                          setRoutesToCompare([]);
                          
                          // Limpar o mapa
                          if (markersRef.current.length > 0) {
                            markersRef.current.forEach(marker => {
                              if (marker) marker.setMap(null);
                            });
                            markersRef.current = [];
                          }
                        }}
                        className="px-2 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                      >
                        Sair do modo comparação
                      </button>
                    )}
                  </div>
                ) : null}
                
                {compareMode && routesToCompare.length > 0 && (
                  <div className="bg-white dark:bg-dark-surface p-2 border-b border-gray-200 dark:border-dark-border">
                    <div className="flex flex-wrap gap-3">
                      {routesToCompare.map((rota, index) => (
                        <div key={index} className="flex items-center">
                          <div 
                            className="w-4 h-4 rounded-full mr-2" 
                            style={{ backgroundColor: rota.cor || getColorForRoute(index) }}
                          ></div>
                          <span className="text-sm text-text dark:text-dark-text">{rota.nome}</span>
                        </div>
                      ))}
                      <div className="flex items-center ml-4 border-l pl-4 border-gray-300 dark:border-dark-border">
                        <div className="flex items-center">
                          <div 
                            className="w-4 h-4 rounded-full mr-2" 
                            style={{ backgroundColor: '#FF9900' }}
                          ></div>
                          <span className="text-sm text-text dark:text-dark-text">Interseção de rotas</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                
                <div className="flex-1 relative">
                  <div 
                    ref={mapRef} 
                    id="mapContainer"
                    className="absolute inset-0"
                    style={{ width: '100%', height: '100%' }}
                  ></div>
                  
                  {/* Overlay com instruções quando nenhuma rota estiver selecionada */}
                  {!selectedRota && (!compareMode || routesToCompare.length === 0) && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <div className="bg-white dark:bg-dark-surface bg-opacity-75 dark:bg-opacity-75 p-6 rounded-lg shadow-lg text-center max-w-md pointer-events-auto">
                        <div className="w-16 h-16 bg-gray-300 dark:bg-dark-border rounded-full flex items-center justify-center mx-auto mb-4">
                          <img src="/icons/rout.svg" alt="Selecionar Rota" className="w-8 h-8 opacity-50" />
                        </div>
                        <h3 className="text-lg font-medium text-textSecondary dark:text-dark-textSecondary mb-2">
                          {compareMode ? "Selecione rotas para comparar" : "Selecione uma rota"}
                        </h3>
                        <p className="text-textSecondary dark:text-dark-textSecondary">
                          {compareMode 
                            ? "Clique nas rotas na lista para adicioná-las à comparação" 
                            : "Escolha uma rota da lista para visualizar no mapa"
                          }
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Modal para adicionar/editar rota */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-dark-surface rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-bold text-text dark:text-dark-text">
                  {formData.nome ? `Editar Rota: ${formData.nome}` : 'Nova Rota'}
                </h2>
                <button 
                  onClick={handleCloseModal}
                  className="text-textSecondary dark:text-dark-textSecondary hover:text-text dark:hover:text-dark-text"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-text dark:text-dark-text mb-1">
                    Grupo *
                  </label>
                  <select
                    name="opcao"
                    value={formData.opcao}
                    onChange={handleInputChange}
                    className={`w-full p-2 rounded-lg border ${formErrors.opcao ? 'border-red-500' : 'border-gray-200 dark:border-dark-border'} bg-white dark:bg-dark-card text-text dark:text-dark-text text-sm`}
                  >
                    <option value="">Selecione um grupo</option>
                    {Object.keys(rotasData).map((group) => (
                      <option key={group} value={group}>
                        {group}
                      </option>
                    ))}
                  </select>
                  {formErrors.opcao && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.opcao}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-text dark:text-dark-text mb-1">
                    Nome da Rota *
                  </label>
                  <input
                    type="text"
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    className={`w-full p-2 rounded-lg border ${formErrors.nome ? 'border-red-500' : 'border-gray-200 dark:border-dark-border'} bg-white dark:bg-dark-card text-text dark:text-dark-text text-sm`}
                    placeholder="Ex: V01"
                  />
                  {formErrors.nome && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.nome}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-text dark:text-dark-text mb-1">
                    Origem
                  </label>
                  <input
                    type="text"
                    name="origem"
                    value={formData.origem}
                    onChange={handleInputChange}
                    className="w-full p-2 rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card text-text dark:text-dark-text text-sm"
                    placeholder="Ex: SAL EXPRESS"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-text dark:text-dark-text mb-1">
                    Destino *
                  </label>
                  <input
                    type="text"
                    name="destino"
                    value={formData.destino}
                    onChange={handleInputChange}
                    className={`w-full p-2 rounded-lg border ${formErrors.destino ? 'border-red-500' : 'border-gray-200 dark:border-dark-border'} bg-white dark:bg-dark-card text-text dark:text-dark-text text-sm`}
                    placeholder="Ex: POCOS DE CALDAS"
                  />
                  {formErrors.destino && (
                    <p className="mt-1 text-sm text-red-500">{formErrors.destino}</p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-text dark:text-dark-text mb-1">
                    Modo
                  </label>
                  <select
                    name="modo"
                    value={formData.modo}
                    onChange={handleInputChange}
                    className="w-full p-2 rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card text-text dark:text-dark-text text-sm"
                  >
                    <option value="DRIVING">DRIVING</option>
                    <option value="WALKING">WALKING</option>
                    <option value="BICYCLING">BICYCLING</option>
                    <option value="TRANSIT">TRANSIT</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-text dark:text-dark-text mb-1">
                    Cor
                  </label>
                  <div className="flex items-center">
                    <input
                      type="color"
                      name="cor"
                      value={formData.cor}
                      onChange={handleInputChange}
                      className="w-12 h-8 p-0 rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card"
                    />
                    <input
                      type="text"
                      name="cor"
                      value={formData.cor}
                      onChange={handleInputChange}
                      className="w-full p-2 ml-2 rounded-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card text-text dark:text-dark-text text-sm"
                      placeholder="Ex: #0b1a99"
                    />
                  </div>
                </div>
              </div>
              
              <div className="mb-6">
                <label className="block text-sm font-medium text-text dark:text-dark-text mb-1">
                  Paradas
                </label>
                <div className="flex items-center">
                  <input
                    type="text"
                    value={novaParada}
                    onChange={(e) => setNovaParada(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleAddParada()}
                    className="flex-1 p-2 rounded-l-lg border border-gray-200 dark:border-dark-border bg-white dark:bg-dark-card text-text dark:text-dark-text text-sm"
                    placeholder="Digite uma cidade e pressione Enter"
                  />
                  <button
                    type="button"
                    onClick={handleAddParada}
                    className="p-2 bg-salBlue text-white rounded-r-lg hover:bg-salBlue/90 transition-colors"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
                    </svg>
                  </button>
                </div>
                
                {formData.paradas.length > 0 ? (
                  <div className="mt-3 p-3 border border-gray-200 dark:border-dark-border rounded-lg max-h-40 overflow-y-auto">
                    <ul className="space-y-1">
                      {formData.paradas.map((parada, index) => (
                        <li key={index} className="flex items-center justify-between p-1 hover:bg-gray-50 dark:hover:bg-dark-card rounded">
                          <div className="flex items-center">
                            <div className="w-2 h-2 bg-salBlue rounded-full mr-2"></div>
                            <span className="text-sm text-text dark:text-dark-text">{parada}</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => handleRemoveParada(index)}
                            className="text-red-500 hover:text-red-700 transition-colors"
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <p className="mt-2 text-sm text-textSecondary dark:text-dark-textSecondary">
                    Nenhuma parada adicionada
                  </p>
                )}
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={handleCloseModal}
                  className="px-4 py-2 border border-gray-200 dark:border-dark-border text-text dark:text-dark-text rounded-lg hover:bg-gray-50 dark:hover:bg-dark-card transition-colors"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={isSaving}
                  className="px-4 py-2 bg-salBlue text-white rounded-lg hover:bg-salBlue/90 transition-colors disabled:opacity-50"
                >
                  {isSaving ? 'Salvando...' : 'Salvar Rota'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}